import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import {
  addAssistant,
  getAssistant,
} from "../../features/assistant/assistantSlice";
import { getPlatformsByAccount } from "../../features/platforms/platformsSlice";
import { getTwilioDetails } from "../../features/twilio/twilioSlice";
import {
  assistantInitialValues,
  assistantValidationSchema,
} from "../../constants/assistant.constants";
import { Icons } from "../../constants/icon-library";

const NewAssistant = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { user } = useSelector((state) => state.user);
  const { platforms = [] } = useSelector((state) => state.platforms || {});

  const validationSchema = assistantValidationSchema;

  const formik = useFormik({
    initialValues: {
      ...assistantInitialValues,
      phoneId: "",
      receivingNumber: "",
      selectedConfiguration: "default",
      isPublic: false,
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      const data = {
        name: values.assistantName,
        agentConfiguration: "default",
        agentFirstMessage: values.firstMessage,
        platform: values.selectedPlatformUuid || null,
        agentType: "platform",
        phoneId: values.phoneId || null,
        isPublic: values.isPublic,
        accountId: user.org,
      };
      console.log("🚀 ~ NewAssistant ~ data:", data);

      try {
        const res = await dispatch(addAssistant(data)).unwrap();
        await dispatch(getAssistant(user.org));
        navigate(`/my-assistants/${res.uuid}`);
        toast.success("Assistant created successfully.");
      } catch (err) {
        console.error("Failed to create assistant:", err);
        toast.error("Failed to create assistant.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (user?.org) {
      dispatch(getPlatformsByAccount(user.org));
      dispatch(getTwilioDetails(user.org));
    }

    if (window.feather) window.feather.replace();
  }, [user, dispatch]);

  return (
    <div className="content">
      <div className="row">
        <div className="card border p-0">
          <div className="card-header p-4 py-4 border-bottom bg-body d-flex justify-content-between align-items-center">
            <h3 className="mb-0 d-flex align-items-center">
              <i className={`wb ${Icons("add")} fs-8 me-2 align-middle`}></i>
              Create New Assistant
            </h3>
          </div>

          <div className="card-body">
            <div className="col-12">
              <form onSubmit={formik.handleSubmit}>
                <div className="mb-4">
                  <label className="fw-bold mb-2">
                    Assistant Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${
                      formik.touched.assistantName &&
                      formik.errors.assistantName
                        ? "is-invalid"
                        : ""
                    }`}
                    name="assistantName"
                    placeholder="Enter assistant name"
                    value={formik.values.assistantName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.assistantName &&
                    formik.errors.assistantName && (
                      <div className="invalid-feedback">
                        {formik.errors.assistantName}
                      </div>
                    )}
                </div>

                <div className="mb-4 d-none">
                  <label className="fw-bold mb-2">
                    First Message <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className={`form-control ${
                      formik.touched.firstMessage && formik.errors.firstMessage
                        ? "is-invalid"
                        : ""
                    }`}
                    rows="3"
                    placeholder="Enter the first message..."
                    name="firstMessage"
                    value={formik.values.firstMessage}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.firstMessage &&
                    formik.errors.firstMessage && (
                      <div className="invalid-feedback">
                        {formik.errors.firstMessage}
                      </div>
                    )}
                </div>

                {/* Default Configuration UI removed as per requirement */}

                {
                  <div className="mb-4">
                    <label className="fw-bold mb-2 d-flex align-items-center">
                      Platform <span className="text-danger">*</span>
                    </label>
                    <select
                      className={`form-control ${
                        formik.touched.selectedPlatformUuid &&
                        formik.errors.selectedPlatformUuid
                          ? "is-invalid"
                          : ""
                      }`}
                      name="selectedPlatformUuid"
                      value={formik.values.selectedPlatformUuid}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    >
                      <option value="">Select Platform</option>

                      {platforms.map((platform) => (
                        <option key={platform.uuid} value={platform.uuid}>
                          {platform.name}
                        </option>
                      ))}
                    </select>

                    {formik.touched.selectedPlatformUuid &&
                      formik.errors.selectedPlatformUuid && (
                        <div className="invalid-feedback">
                          {formik.errors.selectedPlatformUuid}
                        </div>
                      )}
                  </div>
                }

                <div className="mb-4">
                  <label className="fw-bold mb-2 d-block">
                    Visibility <span className="text-danger">*</span>
                  </label>
                  <div className="d-flex align-items-center gap-2">
                    <span className="text-muted">
                      {formik.values.isPublic ? "Public" : "Private"}
                    </span>
                    <div className="form-check form-switch m-0 p-0">
                      <input
                        className="form-check-input m-2"
                        type="checkbox"
                        id="isPublicSwitch"
                        checked={formik.values.isPublic}
                        onChange={(e) =>
                          formik.setFieldValue("isPublic", e.target.checked)
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-between">
                  <button
                    className="btn btn-outline-danger px-3"
                    type="button"
                    onClick={() => navigate(-1)}
                  >
                    <i
                      className={`wb ${Icons("cancel")} fs-8 me-2 align-middle`}
                    ></i>
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    type="submit"
                    disabled={formik.isSubmitting}
                  >
                    <i
                      className={`wb ${Icons("save")} fs-8 me-2 align-middle`}
                    ></i>
                    {formik.isSubmitting ? "Creating..." : "Create Assistant"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewAssistant;
